import i18n from '@/i18n';
import { serializeImageParams } from '@/config/agentImageOptions';
import { imageSpaceAPI } from '@/utils/api';
import { ensureImageSpaceItemsServerUrl, pickUrlFromImageStorageResponse, resolveImageUrlToBlob, resolveFetchableImageUrl } from '@/utils/imageUrlUtils';
import { imageStorageAPI } from '@/utils/api';

export function parseAttachmentsJson(raw) {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => item?.url);
  } catch {
    return [];
  }
}

export function canSubmitImageRequest(message, attachments = []) {
  const trimmed = String(message || '').trim();
  const list = Array.isArray(attachments) ? attachments : parseAttachmentsJson(attachments);
  return Boolean(trimmed) || list.length > 0;
}

export function buildAttachmentsJsonForApi(attachments = []) {
  const list = Array.isArray(attachments) ? attachments : parseAttachmentsJson(attachments);
  const normalized = list
    .filter((item) => item?.url)
    .map(({ name, url }) => ({ name, url }));
  return normalized.length ? JSON.stringify(normalized) : null;
}

export function buildImageParamsJsonForApi(imageParams) {
  if (!imageParams) return null;
  return serializeImageParams(imageParams);
}

export function parseArtifactUrlsJson(raw) {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => String(item || '').trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

/** 助手消息优先用持久化后的 /uploads/ 地址，避免 DashScope 临时 URL 过期后刷新一直 loading。 */
export function buildAssistantDisplayContent(content, artifactUrlsJson) {
  const text = String(content || '');
  const stableUrls = parseArtifactUrlsJson(artifactUrlsJson);
  if (!stableUrls.length) {
    return text;
  }

  let imageIndex = 0;
  const replaced = text.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (full, alt, url) => {
    const stableUrl = stableUrls[imageIndex];
    imageIndex += 1;
    if (!stableUrl) {
      return full;
    }
    return `![${alt}](${stableUrl})`;
  });

  if (imageIndex > 0) {
    return replaced;
  }

  const prefix = text.trim() || '已为您生成图片。';
  const gallery = stableUrls.map((url, index) => `![生成结果 ${index + 1}](${url})`).join('\n\n');
  return `${prefix}\n\n${gallery}`;
}

export function buildImageLoadingHint(message, attachments, imageParams) {
  const trimmed = (message || '').trim();
  const hasImage = Array.isArray(attachments) && attachments.length > 0;
  const count = Number(imageParams?.count) > 1 ? Number(imageParams.count) : 1;
  const countLabel = count > 1 ? `${count} 张` : '图片';

  if (hasImage && trimmed) {
    return i18n.t('workbench:ipeUtilError.agentProcessing');
  }
  if (hasImage) {
    return i18n.t('workbench:ipeUtilError.agentOptimizing');
  }
  if (trimmed) {
    const short = trimmed.length > 48 ? `${trimmed.slice(0, 48)}…` : trimmed;
    return count > 1
      ? `好的，正在根据您的描述生成 ${countLabel}：${short}`
      : `好的，正在根据您的描述生成图片：${short}`;
  }
  return count > 1 ? `好的，正在为您生成 ${countLabel}。` : '好的，正在为您生成图片。';
}

export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function normalizeImageSpaceUploadUrl(rawUrl) {
  const raw = String(rawUrl || '').trim();
  if (!raw) return '';
  if (raw.startsWith('/uploads/')) return raw;
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    try {
      const path = new URL(raw).pathname || '';
      return path.startsWith('/uploads/') ? path : '';
    } catch {
      return '';
    }
  }
  return '';
}

function buildAgentImageFilename(alt, url) {
  const base = String(alt || 'Agent 生成').replace(/[\\/:*?"<>|]/g, '_').trim() || 'Agent 生成';
  const extMatch = String(url || '').match(/\.(png|jpe?g|webp|gif)(?:\?|$)/i);
  const ext = extMatch ? extMatch[1].toLowerCase() : 'png';
  return base.endsWith(`.${ext}`) ? base : `${base}.${ext}`;
}

function loadImageFromBlob(blob) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('图片加载失败'));
    };
    image.src = objectUrl;
  });
}

async function addIpeWatermarkToBlob(blob) {
  const image = await loadImageFromBlob(blob);
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(i18n.t('workbench:ipeUtilError.cannotCreateCanvas'));

  ctx.drawImage(image, 0, 0);

  const shortEdge = Math.min(canvas.width, canvas.height);
  const fontSize = Math.max(18, Math.round(shortEdge * 0.045));
  const padding = Math.round(fontSize * 0.7);
  const text = 'IPE';

  ctx.font = `700 ${fontSize}px system-ui, -apple-system, "Segoe UI", sans-serif`;
  const metrics = ctx.measureText(text);
  const textWidth = metrics.width;
  const textHeight = fontSize;
  const x = canvas.width - padding;
  const y = canvas.height - padding;
  const bgPaddingX = Math.round(fontSize * 0.35);
  const bgPaddingY = Math.round(fontSize * 0.25);
  const bgX = x - textWidth - bgPaddingX;
  const bgY = y - textHeight - bgPaddingY;
  const bgW = textWidth + bgPaddingX * 2;
  const bgH = textHeight + bgPaddingY * 2;

  ctx.fillStyle = 'rgba(15, 23, 42, 0.42)';
  if (typeof ctx.roundRect === 'function') {
    ctx.beginPath();
    ctx.roundRect(bgX, bgY, bgW, bgH, Math.round(fontSize * 0.2));
    ctx.fill();
  } else {
    ctx.fillRect(bgX, bgY, bgW, bgH);
  }

  ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.textBaseline = 'bottom';
  ctx.textAlign = 'right';
  ctx.fillText(text, x, y);

  const mimeType = blob.type === 'image/png' ? 'image/png' : 'image/jpeg';
  return new Promise((resolve, reject) => {
    canvas.toBlob((result) => {
      if (result) resolve(result);
      else reject(new Error(i18n.t('workbench:ipeUtilError.cannotCreateCanvas')));
    }, mimeType, mimeType === 'image/jpeg' ? 0.92 : undefined);
  });
}

export async function downloadAgentImage(url, alt = i18n.t('workbench:studioChrome.generatedImage')) {
  const rawUrl = String(url || '').trim();
  if (!rawUrl) throw new Error(i18n.t('workbench:ipeUtilError.invalidImageUrlShort'));

  const blob = await resolveImageUrlToBlob(rawUrl);
  const watermarkedBlob = await addIpeWatermarkToBlob(blob);
  const objectUrl = URL.createObjectURL(watermarkedBlob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = buildAgentImageFilename(alt, rawUrl);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

export async function moveAgentImageToImageSpace(url, alt = i18n.t('workbench:studioChrome.generatedImage')) {
  const rawUrl = String(url || '').trim();
  if (!rawUrl) throw new Error(i18n.t('workbench:ipeUtilError.invalidImageUrlShort'));

  let storageUrl = rawUrl;
  if (!rawUrl.startsWith('/uploads/')) {
    const result = await imageStorageAPI.save(
      rawUrl.startsWith('data:') || rawUrl.startsWith('blob:')
        ? rawUrl
        : (await resolveFetchableImageUrl(rawUrl) || ''),
      { filename: buildAgentImageFilename(alt, rawUrl), folder: 'image-space', registerImageSpace: false },
    );
    storageUrl = pickUrlFromImageStorageResponse(result);
    if (!storageUrl) throw new Error(i18n.t('workbench:ipeUtilError.imageUploadFailedShort'));
  }

  const id = `agent_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const now = new Date().toISOString();
  const item = {
    id,
    name: buildAgentImageFilename(alt, rawUrl),
    url: storageUrl,
    source: 'agent',
    createdAt: now,
    uploadedAt: now,
  };

  const converted = await ensureImageSpaceItemsServerUrl([item]);
  const toSave = converted
    .map((entry) => {
      const normalizedUrl = normalizeImageSpaceUploadUrl(entry?.url);
      if (!normalizedUrl) return null;
      return { ...entry, url: normalizedUrl };
    })
    .filter(Boolean);

  if (!toSave.length) {
    throw new Error(i18n.t('workbench:ipeUtilError.moveToImageSpaceFailed'));
  }

  await imageSpaceAPI.batchSave(toSave);
  return toSave[0];
}

export function resolveAgentAttachmentUrl(url) {
  const rawUrl = String(url || '').trim();
  if (!rawUrl) return '';
  if (rawUrl.startsWith('data:') || rawUrl.startsWith('blob:')) return rawUrl;
  if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) return rawUrl;
  return rawUrl;
}
